/* @flow */
import jwtDecode from 'jwt-decode'
import Store from 'lockr'
import http from 'axios'
import { root } from './conf'
import _ from 'lodash'
import { HttpErrorEnum } from 'enums'
import { AbilityBuilder, Ability } from '@casl/ability'
import Vue from 'vue'

type JWT = {
  app: boolean,
  exp: number,
  iat: number,
  iss: string,
  sub: string,
  super?: boolean
}

function extractRulesFromToken(token: string) {
  const { rules, can: allow, cannot: forbid } = AbilityBuilder.extract()

  let decodedToken = decodeToken(token)

  if (decodedToken) {
    if (decodedToken.super) {
      // Only supers are allowed to edit all users
      allow('edit', 'user')
      allow('create', 'user')
      allow('delete', 'user')
      // Only supers are allowed to make/unmake supers
      allow('make', 'super')
      allow('access', 'manatee_settings')
      allow('access', 'flow_restrictions')
      allow('access', 'estero')
    }

    // Users are allowed to edit themselves (excludnig super status)
    allow('edit', 'user', decodedToken.sub)
  }

  return rules
}

/**
 * Verifies a JWT token.
 * @function
 * @memberof AuthService
 *
 * @return { boolean }
 */
const checkToken = (token: string): boolean => {
  let decodedToken = decodeToken(token)
  return decodedToken ? decodedToken.exp >= parseInt(Date.now() / 1000) : false
}

/**
 * Decodes a JWT token.
 * @function
 * @memberof AuthService
 *
 * @return { JWT }
 */
const decodeToken = (token: string): ?JWT => {
  try {
    return jwtDecode(token)
  } catch (e) {
    console.error('Could not decode JWT token')
    return null
  }
}

/**
 * Stores a JWT token into local storage.
 * @function
 * @memberof AuthService
 *
 * @return { any }
 */
const saveToken = (token: string, persistentUser: boolean): any => {
  Store.set('token', token)
  Store.set('persistentUser', persistentUser)
}

/**
 * Stores credentials as a base64 encoded string.
 *
 * @function
 * @memberof AuthService
 *
 * @return { any }
 */
const saveCredentials = (username: string, password: string): any => {
  Store.set('credentials', btoa(`${username}:${password}`))
}

/**
 * Deletes a JWT token from local storage.
 * @function
 * @memberof AuthService
 *
 * @return { any }
 */
const deleteToken = (): any => {
  Store.set('token', null)
  Store.set('persistentUser', false)
}

/**
 * Deletes any saved credentials.
 * @function
 * @memberof AuthService
 *
 * @return { any }
 */
const deleteCredentials = (): any => {
  Store.set('credentials', null)
}
/**
 * Gets the JWT token value from local storage.
 * @function
 * @memberof AuthService
 *
 * @return { string }
 */
const getToken = (): string => Store.get('token') || ''

const isUserSuper = (): ?boolean => {
  const rawToken = getToken()
  const decodedToken = rawToken ? decodeToken(rawToken) : null
  return decodedToken ? decodedToken.super : false
}

/**
 * Gets the saved (base64 encoded) credentials from local storage.
 *
 * @function
 * @memberof AuthService
 *
 * @return { string }
 */
const getCredentials = (): string => Store.get('credentials') || ''

/**
 * Autenticates against a username and password.
 * @function
 * @memberof AuthService
 *
 * @return { Promise<string> }. The string token representation.
 */
const authenticate = (username: string, password: string): Promise<string> => {
  const body = {
    username: username,
    password: password
  }
  return http
    .post(`${root}/authenticate`, body)
    .then(res => {
      if (res && res.data) return { ...res.data }
      else throw HttpErrorEnum.UNAUTHORIZED
    })
    .catch(err => {
      if (!err.response) throw HttpErrorEnum.SERVER_ERROR
      if (err.response.status === 401) throw HttpErrorEnum.UNAUTHORIZED
      else throw err.response.data.Error
    })
}

/**
 * Sets the global (common) authorization header in the http library.
 * @function
 * @memberof AuthService
 */
const setAuthorizationHeader = (header: string) =>
  (http.defaults.headers.common['Authorization'] = `Bearer ${header}`)

/**
 * Gets the name of the logged in user.
 * @function
 * @memberof AuthService
 *
 * @return { string }
 */
const getLoggedInUser = (): string => {
  const token = Store.get('token')
  return token && jwtDecode(token || '').sub
}

/**
 * Checks if the user is logged in by validating that the JWT token is not empty
 * @function
 * @memberof AuthService
 *
 * @return { boolean }
 */
const isUserLoggedIn = (): boolean => {
  const token = getToken()
  return !_.isEmpty(token) && !jwtDecode(token).otp
}

const isUserPersistent = (): boolean => {
  return Store.get('persistentUser')
}

/**
 * Deletes the jwt token and logs out the user
 * @function
 * @memberof AuthService
 *
 * @return { void }
 */
const logOut = (params: Object): void => {
  if (!isUserPersistent()) {
    deleteToken()
    deleteCredentials()
    Vue.router.replace({ name: 'login', ...(params || { query: { status: 401 } }) })
  }
}

const init2FA = (): Promise<Object> => {
  const url = `${root}/2fa/init`
  return http.get(url).then(res => res.data)
}

const enable2FA = (secret: String, totp: String): Promise<Object> => {
  const url = `${root}/2fa/enable`
  return http.post(url, { secret, totp }).then(res => res.data)
}

const verifyTOTP = (totp: String): Promise<Object> => {
  const url = `${root}/2fa/validate/totp`
  return http.post(url, { totp }).then(res => res.data)
}

const self = {
  extractRulesFromToken,
  authenticate,
  isUserPersistent,
  isUserSuper,
  logOut,
  checkToken,
  saveToken,
  getToken,
  deleteToken,
  setAuthorizationHeader,
  getLoggedInUser,
  isUserLoggedIn,
  saveCredentials,
  getCredentials,
  deleteCredentials,
  twoFactor: {
    init: init2FA,
    enable: enable2FA,
    verify: verifyTOTP
  }
}

/**
 * Auth service: contains authentication & JWT logic.
 * @module GroupService
 * @namespace AuthService
 * @version 1.0.0
 */
export default self
